// GLSL for hologram glyphs and feedback lines (docs/ART-DIRECTION.md §1 holograms, §2.5 glow discipline, §4
// photosensitivity). InstancedMesh defines USE_INSTANCING / USE_INSTANCING_COLOR, so `instanceMatrix` and
// `instanceColor` come from the ShaderMaterial prefix. All outputs are additive.

/**
 * One shared hologram material for every glyph batch: per-instance faction tint and flicker phase. Instance
 * matrices carry a uniform scale, so normals go through the instance basis directly.
 */
export const HOLO_VERTEX = /* glsl */ `
attribute float aPhase;

varying vec3 vColor;
varying vec3 vNormal;
varying vec3 vView;
varying float vPhase;

void main() {
  vec4 mvPosition = modelViewMatrix * instanceMatrix * vec4(position, 1.0);
  vNormal = normalize(normalMatrix * (mat3(instanceMatrix) * normal));
  vView = normalize(-mvPosition.xyz);
  vColor = instanceColor;
  vPhase = aPhase;
  gl_Position = projectionMatrix * mvPosition;
}
`

/**
 * Fresnel rim glow over a dim fill, horizontal screen-space scanlines and a slow sine flicker. uMotion = 0
 * ("Reduce effects") stops the scanline scroll and the flicker. Both stay under 3 Hz: the scroll cycles at
 * speed / period and the flicker is a smooth sine at uFlickerHz.
 */
export const HOLO_FRAGMENT = /* glsl */ `
uniform float uTime;
uniform float uMotion;
uniform float uFill;
uniform float uRim;
uniform float uRimPower;
uniform float uScanPeriodPx;
uniform float uScanSpeedPx;
uniform float uScanDepth;
uniform float uFlickerHz;
uniform float uFlickerDepth;

varying vec3 vColor;
varying vec3 vNormal;
varying vec3 vView;
varying float vPhase;

const float TAU = 6.28318530718;

void main() {
  float facing = abs(dot(normalize(vNormal), normalize(vView)));
  float glow = uFill + uRim * pow(1.0 - facing, uRimPower);
  float scan = 1.0 - uScanDepth * step(0.5, fract((gl_FragCoord.y + uTime * uScanSpeedPx * uMotion) / uScanPeriodPx));
  float flicker = 1.0 - uFlickerDepth * uMotion * (0.5 + 0.5 * sin(TAU * (uTime * uFlickerHz + vPhase)));
  gl_FragColor = vec4(vColor * glow * scan * flicker, 1.0);
}
`

/**
 * Owner rim at the foot of a unit or building (ART-DIRECTION §2.1): a flat ring whose instance matrix sizes it with its
 * glyph. aRimDashes encodes the owner by shape: 0 = solid, otherwise that many dashes around the ring.
 */
export const GLYPH_RIM_VERTEX = /* glsl */ `
attribute float aRimDashes;

varying vec3 vColor;
varying vec2 vLocal;
varying float vDashes;

void main() {
  vLocal = position.xy;
  vDashes = aRimDashes;
  vColor = instanceColor;
  gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
}
`

export const GLYPH_RIM_FRAGMENT = /* glsl */ `
uniform float uIntensity;
uniform float uDashDuty;

varying vec3 vColor;
varying vec2 vLocal;
varying float vDashes;

const float TAU = 6.28318530718;

void main() {
  float turn = atan(vLocal.y, vLocal.x) / TAU + 0.5;
  float dashOn = step(fract(turn * vDashes), uDashDuty);
  float visible = mix(1.0, dashOn, step(0.5, vDashes));
  if (visible < 0.5) discard;
  gl_FragColor = vec4(vColor * uIntensity, 1.0);
}
`

/** Solid feedback lines (lock-on lines, air drop lines): per-vertex colour × intensity. */
export const LINE_VERTEX = /* glsl */ `
attribute vec3 aColor;

varying vec3 vColor;

void main() {
  vColor = aColor;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

export const LINE_FRAGMENT = /* glsl */ `
uniform float uIntensity;

varying vec3 vColor;

void main() {
  gl_FragColor = vec4(vColor * uIntensity, 1.0);
}
`
